import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, apiErr } from "@/context/AuthContext";
import { getPortal } from "@/lib/portal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import InstallPWA from "@/components/public/InstallPWA";
import { toast } from "sonner";
import { Loader2, LayoutDashboard, Store, ArrowLeft } from "lucide-react";

const PORTALS = {
  admin: { label: "Panel Admin", host: "admin.tudominio.com", icon: LayoutDashboard, tagline: "Supervisa todos tus restaurantes, ventas en tiempo real y comisión de plataforma." },
  restaurant: { label: "Panel Restaurante", host: "restaurant.tudominio.com", icon: Store, tagline: "Gestiona menú, pedidos e inventario. Instálalo como app en tu tablet." },
};

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const portal = getPortal();
  const meta = PORTALS[portal] || null;
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const routeByRole = (u) => {
    if (u.role === "superadmin") nav("/admin");
    else if (u.role === "owner") nav("/owner");
    else nav("/");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = mode === "login" ? await login(email, password) : await register(email, password, name);
      toast.success(`Bienvenido, ${u.name || u.email}`);
      routeByRole(u);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const fill = (em, pw) => { setEmail(em); setPassword(pw); setMode("login"); };
  const Icon = meta?.icon || Store;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col lg:flex-row font-adminbody">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 bg-neutral-950 text-neutral-100 relative">
        <Link to="/" className="relative flex items-center">
          <img src="/domifood-logo.png" alt="Domifood" className="h-9 w-auto" />
        </Link>
        <div className="relative">
          {meta && (
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/15 border border-primary/30 px-4 py-1.5 text-sm text-primary font-semibold mb-6">
              <Icon className="w-4 h-4" /> {meta.label}
            </div>
          )}
          <h2 className="font-admin font-black text-4xl leading-tight">
            {meta ? meta.label : <>Gestiona tu red<br />de restaurantes.</>}
          </h2>
          <p className="mt-4 text-neutral-400 max-w-sm">{meta ? meta.tagline : "Panel central conectado a Supabase y Stripe Connect. Ventas en tiempo real y comisión automática."}</p>
          {meta && <p className="mt-6 text-xs font-mono text-neutral-500">{meta.host}</p>}
        </div>
        <div className="relative text-sm text-neutral-500">© Domifood Platform</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8 text-sm text-muted-foreground"><ArrowLeft className="w-4 h-4" /> Volver al inicio</Link>

          {meta && (
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-sm text-primary font-semibold">
              <Icon className="w-4 h-4" /> {meta.label}
            </div>
          )}
          <h1 className="font-admin font-black text-3xl mb-1" data-testid="login-title">
            {mode === "login" ? "Iniciar sesión" : "Crear cuenta"}
          </h1>
          <p className="text-muted-foreground text-sm mb-6">
            {mode === "login" ? "Accede a tu panel." : "Regístrate como cliente."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Nombre</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name-input" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Correo</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Contraseña</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
            </div>
            <Button type="submit" className="w-full h-11 rounded-full font-bold" disabled={loading} data-testid="login-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Entrar" : "Registrarme"}
            </Button>
          </form>

          <button
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="toggle-mode-btn"
          >
            {mode === "login" ? "¿No tienes cuenta? Regístrate" : "¿Ya tienes cuenta? Inicia sesión"}
          </button>

          {portal === "restaurant" && (
            <div className="mt-6 rounded-xl border-2 border-border p-4">
              <p className="text-sm font-medium mb-3">Usa el panel como app dedicada en tu tablet:</p>
              <InstallPWA />
            </div>
          )}

          <div className="mt-8 rounded-xl border border-border bg-neutral-50 p-4 text-sm">
            <p className="text-muted-foreground mb-3 font-medium">Cuentas de prueba:</p>
            <div className="space-y-2">
              {portal !== "restaurant" && (
                <button onClick={() => fill("shotstudio1229@gmail.com", "Admin123!")}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-neutral-100 transition-colors" data-testid="demo-admin-btn">
                  <span className="text-primary font-bold">Super Admin</span> · shotstudio1229@gmail.com
                </button>
              )}
              {portal !== "admin" && (
                <button onClick={() => fill("owner@bellavista.com", "Owner123!")}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-neutral-100 transition-colors" data-testid="demo-owner-btn">
                  <span className="text-emerald-600 font-bold">Propietario</span> · owner@bellavista.com
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
